export class PromiseTimedOutError extends Error {}

type TimeoutId = ReturnType<typeof setTimeout>

/**
 * Races a given promise against a timer. If the promise does not settle
 * (resolve or reject) within the specified time, the returned promise will
 * reject with a PromiseTimedOutError.
 */
export function promiseWithTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  let timeoutId: TimeoutId | null = null // Stores the ID of the timeout to allow clearing it

  const clearTimeoutIfSet = () => {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
  }

  // Create a promise that will reject if the timeout duration is exceeded.
  const timeout = new Promise<T>((_, reject) => {
    timeoutId = setTimeout(() => {
      // If the timeout fires, clear the timeout to prevent any potential lingering issues
      // (though at this point it's technically already fired).
      // Then, reject with our custom timeout error.
      clearTimeoutIfSet()

      reject(new PromiseTimedOutError(`Promise timed out after ${ms}ms.`))
    }, ms)
  })

  // Attach a .then() handler to the original promise.
  // If the original promise settles (resolves or rejects) *before* the timeout,
  // we clear the pending timeout to prevent it from unnecessarily firing later.
  promise.then(
    // On success, clear the timeout
    () => clearTimeoutIfSet(),
    // On rejection, also clear the timeout
    () => clearTimeoutIfSet()
  )

  // Use Promise.race to return the result of whichever promise settles first:
  // either the original promise or the timeout promise.
  return Promise.race([promise, timeout])
}
